import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CheckIcon, CopyIcon, GiftIcon, MinusIcon, XIcon } from 'lucide-react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Alert, Button, Input, Modal, Skeleton, TextArea } from '@repo/ui'
import { validateEmailAddress } from '@repo/types'

// Direct subpath, not the `components/input` barrel (which reaches back into feedback).
import { EmailChipInput } from 'components/input/fields/EmailChipInput'
import { useSnackbar } from 'contexts'
import {
  INVITE_OUTCOME_COPY,
  REFERRAL_INVITES_PER_REQUEST,
  REFERRAL_NOTE_MAX,
  referralOfferCopy,
} from 'constants/referrals'
import {
  useMyReferralLink,
  useSendReferralInvites,
  type ReferralOffer,
  type SendReferralInvitesResult,
} from 'hooks/api/useReferrals'
import { ROUTES } from 'routes'
import { statusOf } from 'services'
import { getErrorMessage } from 'utils'

import { REFER_A_BRAND_OPEN_EVENT } from './openReferABrandModal'

export { openReferABrandModal, REFER_A_BRAND_OPEN_EVENT } from './openReferABrandModal'

/**
 * VRG-655 — "Refer & earn" (named "Refer a brand" until VRG-680): the offer, the member's own link, and invite by email.
 *
 * ## The enumeration rule is the API's, and this modal must not undo it
 *
 * The API answers `sent` for an address that is already a Vera customer (or already
 * waiting, or someone else's referral) and quietly does not email it (VRG-654, AC3). So the
 * result list shows "Sent" for those, exactly like a fresh invitation. Nothing here may
 * phrase a result in a way that would let a sender tell the two apart.
 */
export const ReferABrandModal = () => {
  const [open, setOpen] = useState(false)
  // Bumped on every open so the dialog remounts with a clean form: no stale addresses,
  // note or results from the last time.
  const [session, setSession] = useState(0)

  useEffect(() => {
    const handleOpen = () => {
      setSession((n) => n + 1)
      setOpen(true)
    }
    window.addEventListener(REFER_A_BRAND_OPEN_EVENT, handleOpen)
    return () => window.removeEventListener(REFER_A_BRAND_OPEN_EVENT, handleOpen)
  }, [])

  const onClose = useCallback(() => setOpen(false), [])

  return <ReferABrandDialog key={session} open={open} onClose={onClose} />
}

const ReferABrandDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate()
  // Fetched only while open: `me/link` MINTS a code on first call, so fetching it from
  // anywhere that renders on every page would mint one for every member who never refers.
  const { data: link, isLoading, isError, refetch } = useMyReferralLink({ enabled: open })
  const send = useSendReferralInvites()

  const [emails, setEmails] = useState<string[]>([])
  const [pending, setPending] = useState('')
  const [note, setNote] = useState('')
  const [results, setResults] = useState<SendReferralInvitesResult['results'] | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const outcomeRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (results || sendError) outcomeRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [results, sendError])

  const validate = (email: string): string | null => {
    const result = validateEmailAddress(email)
    if (!result.ok) return result.message ?? 'Enter a valid email address.'
    if (emails.length >= REFERRAL_INVITES_PER_REQUEST)
      return `You can invite up to ${REFERRAL_INVITES_PER_REQUEST} addresses at a time.`
    return null
  }

  const canRefer = !!link?.can_refer
  // §54: live only when there is something valid to send, and nothing refused is still
  // sitting in the field (submitting past it would drop it silently).
  const canSend =
    canRefer && emails.length > 0 && pending.trim() === '' && !send.isPending && note.length <= REFERRAL_NOTE_MAX

  const handleSend = async () => {
    if (!canSend) return
    setSendError(null)
    try {
      const data = await send.mutateAsync({ emails, note: note.trim() || undefined })
      setResults(data.results)
      setEmails([])
      setNote('')
    } catch (error) {
      const status = statusOf(error)
      setSendError(
        status === 429
          ? "You've reached today's invitation limit. You can send more tomorrow, or share your link instead."
          : status === 403
            ? 'Invitations cannot be sent from this account.'
            : getErrorMessage(error),
      )
    }
  }

  const seeReferrals = () => {
    onClose()
    navigate(ROUTES.REFERRALS)
  }

  const actions = (
    <>
      <Button variant="text" onClick={seeReferrals} sx={{ px: 0 }}>
        See your referrals
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button variant="secondary" onClick={onClose}>
        {results || !canRefer ? 'Close' : 'Cancel'}
      </Button>
      {canRefer && (
        <Button onClick={handleSend} disabled={!canSend} loading={send.isPending}>
          {emails.length > 1 ? `Send ${emails.length} invitations` : 'Send invitation'}
        </Button>
      )}
    </>
  )

  let body: React.ReactNode
  if (isLoading) {
    body = <ReferABrandSkeleton />
  } else if (isError || !link) {
    body = (
      <Alert
        severity="error"
        title="We couldn't load your referral link."
        button={
          <Button variant="secondary" onClick={() => refetch()}>
            Try again
          </Button>
        }
      />
    )
  } else if (!link.can_refer) {
    body = (
      <Stack spacing={5}>
        <OfferBlock offer={link.offer} />
        <Alert
          severity="info"
          title="Referrals are not available for this account."
          description="Demo and example accounts cannot refer brands."
        />
      </Stack>
    )
  } else {
    body = (
      <Stack spacing={6}>
        {/* The outcome sits FIRST and is scrolled to: below the form it landed under the
            fold of a scrolling dialog, so a send looked like it had done nothing. */}
        {(sendError || results) && (
          <Box ref={outcomeRef} sx={{ scrollMarginTop: 16 }}>
            {sendError ? <Alert severity="error" title={sendError} /> : results && <InviteResults results={results} />}
          </Box>
        )}
        <OfferBlock offer={link.offer} />

        <ReferralLinkField url={link.url ?? ''} />

        <Stack spacing={3}>
          <EmailChipInput
            label="Invite by email"
            value={emails}
            onChange={(next) => {
              setEmails(next)
              setResults(null)
            }}
            validate={validate}
            onPendingChange={setPending}
            disabled={send.isPending}
          />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Up to {REFERRAL_INVITES_PER_REQUEST} addresses. Press Enter, comma or space after each one.
          </Typography>
        </Stack>
        <TextArea
          label="Add a note (optional)"
          value={note}
          onChange={setNote}
          minRows={2}
          maxRows={5}
          placeholder="A line in your own words, quoted in the email."
          error={note.length > REFERRAL_NOTE_MAX}
          helperText={note.length > REFERRAL_NOTE_MAX * 0.8 ? `${note.length} / ${REFERRAL_NOTE_MAX}` : undefined}
          disabled={send.isPending}
        />

      </Stack>
    )
  }

  return (
    <Modal
      title="Refer & earn"
      titleIcon={<GiftIcon size={20} strokeWidth={1} />}
      description="Introduce a brand to Vera. When they join and pay, your company receives activations."
      open={open}
      onClose={onClose}
      sx={{ width: 560 }}
      // The footer wraps on a narrow screen instead of pushing "See your referrals" off the
      // left edge. `gap` replaces the Stack's per-child margin, which would otherwise indent
      // whichever button wraps.
      actionsSx={{ flexWrap: 'wrap', gap: '12px', '& > :not(style) ~ :not(style)': { ml: 0 } }}
      actions={actions}
    >
      {body}
    </Modal>
  )
}

/**
 * The member's own link with a Copy button. Shared by the modal and the referrals page so
 * the two copy the same URL the same way. Long links ellipsize inside the field rather than
 * widening the dialog (§74: a modal never scrolls sideways).
 */
export const ReferralLinkField = ({ url, label = 'Your link' }: { url: string; label?: string }) => {
  const { open: notify } = useSnackbar()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      notify({ type: 'success', title: 'Link copied' })
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      notify({ type: 'error', title: 'Could not copy the link. Select it and copy it instead.' })
    }
  }

  return (
    <Input
      label={label}
      value={url}
      readOnly
      inputProps={{ 'aria-label': 'Your referral link', style: { textOverflow: 'ellipsis' } }}
      endAdornment={
        <Button
          variant="secondary"
          onClick={handleCopy}
          startIcon={copied ? <CheckIcon size={16} strokeWidth={1.5} /> : <CopyIcon size={16} strokeWidth={1.5} />}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      }
    />
  )
}

/** The two sides of the offer, read from the API's numbers. Shared with the page. */
export const OfferBlock = ({ offer }: { offer: ReferralOffer }) => {
  const theme = useTheme()
  const copy = referralOfferCopy(offer)
  const panel = {
    minWidth: 0,
    p: 4,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
  }
  return (
    // A grid, not a wrapping Stack: Stack's `spacing` is a left margin on every child after
    // the first, so once the second panel wraps onto its own row it keeps that margin and
    // sits indented (seen at 375px).
    <Box
      data-testid="referral-offer"
      sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
    >
      <Box sx={panel}>
        <Typography variant="body3" color="text.secondary" sx={{ mb: 2 }}>
          {copy.youTitle}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {copy.youBody}
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, mt: 2 }}>
          {copy.youDetail}
        </Typography>
      </Box>
      <Box sx={panel}>
        <Typography variant="body3" color="text.secondary" sx={{ mb: 2 }}>
          {copy.themTitle}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {copy.themMonthly}
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, mt: 2 }}>
          {copy.themAnnual}
        </Typography>
      </Box>
    </Box>
  )
}

/** The per-address results, phrased neutrally (see `INVITE_OUTCOME_COPY`). */
const InviteResults = ({ results }: { results: SendReferralInvitesResult['results'] }) => {
  const theme = useTheme()
  return (
    <Stack
      spacing={2}
      role="status"
      aria-live="polite"
      sx={{ p: 4, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.04) }}
    >
      {results.map((r) => {
        const copy = INVITE_OUTCOME_COPY[r.outcome] ?? INVITE_OUTCOME_COPY.failed
        const Icon = copy.tone === 'ok' ? CheckIcon : copy.tone === 'error' ? XIcon : MinusIcon
        return (
          <Stack key={r.email} direction="row" spacing={3} alignItems="flex-start">
            <Box sx={{ pt: '2px', color: copy.tone === 'error' ? 'error.main' : 'text.secondary', flexShrink: 0 }}>
              <Icon size={16} strokeWidth={1.5} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {r.email}
              </Typography>
              <Typography variant="body3" color="text.secondary">
                {copy.label}
              </Typography>
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}

/** Footprint parity with the loaded body (§19): intro line, offer pair, link, invite field. */
const ReferABrandSkeleton = () => (
  <Stack spacing={6} aria-busy="true">
    <Skeleton width="80%" height={18} />
    <Stack direction="row" spacing={3}>
      <Skeleton variant="rounded" height={132} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={132} sx={{ flex: 1 }} />
    </Stack>
    <Stack spacing={2}>
      <Skeleton width={80} height={16} />
      <Skeleton variant="rounded" height={49} />
    </Stack>
    <Stack spacing={2}>
      <Skeleton width={110} height={16} />
      <Skeleton variant="rounded" height={49} />
    </Stack>
    <Skeleton variant="rounded" height={88} />
  </Stack>
)
